import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Divider, MenuItem, Paper, Typography } from "@mui/material";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import FormInput from "../Common/FormInputs/MuiTextField";
import AppButton from "../Common/Button/Button";
import AppRadioGroup from "../Common/FormInputs/AppRadioGroup";
import ScreenLoader from "../Common/ScreenLoader/ScreenLoader";
import AppSnackbar from "../Common/Snackbar/AppSnackbar";
import { selectMenuProps } from "../../utils/selectMenuProps";
import { parseCsvFirstColumn } from "../../utils/csvUtils";
import {
  configureInAppNotification,
  fetchAllInAppNotifications,
  getInAppNotificationErrorMessage,
  invalidateInAppNotificationListCache,
} from "../../action/inAppNotification";
import {
  buildConfigurePayload,
  getAssignmentErrorMessage,
  isAssignmentPermissionError,
  isNotificationExpired,
  matchesTargetType,
  validateAssignmentForm,
} from "../../utils/inAppNotificationUtils";
import {
  ASSIGNMENT_PERMISSION_ERROR_MESSAGE,
  ASSIGNMENT_TARGET_OPTIONS,
  getInAppNotificationPermission,
  inAppCardHeaderRowSx,
  inAppFooterActionsSx,
  inAppPrimaryActionSx,
  inAppRadioGroupResponsiveSx,
  inAppSectionIconRowSx,
  sectionCardSx,
  USER_SPECIFIC_NOTIFICATION_EMPTY_MESSAGE,
} from "../../constants/inAppNotificationConstant";
import ChipTextInput from "./ChipTextInput";
import FormSection from "./FormSection";
import FormFieldRow from "./FormFieldRow";
import CsvUploadField from "./CsvUploadField";
import SelectedNotificationSummary from "./SelectedNotificationSummary";
import customConfig from "../../config";
import { useDesignTokens } from "../../theme/useDesignTokens";

const getDefaultTargetBy = (canUserIds, canMobile) => {
  if (canUserIds) return "userIds";
  if (canMobile) return "mobileNo";
  return "userIds";
};

const UserAssignmentsTab = ({ userRoles = [], userLevel = 0 }) => {
  const { colors } = useDesignTokens();

  const canUploadUserIds = getInAppNotificationPermission(
    userRoles,
    "uploadUserIdsCsv",
    userLevel,
  );
  const canUploadMobile = getInAppNotificationPermission(
    userRoles,
    "configureMobileNumbers",
    userLevel,
  );

  const [userNotifications, setUserNotifications] = useState([]);
  const [selectedNotificationId, setSelectedNotificationId] = useState("");
  const [targetBy, setTargetBy] = useState(() =>
    getDefaultTargetBy(canUploadUserIds, canUploadMobile),
  );
  const [userIds, setUserIds] = useState([]);
  const [mobileNumbers, setMobileNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [listFetchError, setListFetchError] = useState("");
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [csvResetKey, setCsvResetKey] = useState(0);
  const [errors, setErrors] = useState({});
  const [apiFeedbackMsg, setApiFeedbackMsg] = useState({
    open: false,
    type: "info",
    msg: "",
  });
  const userIdsInputRef = useRef(null);
  const mobileNumbersInputRef = useRef(null);

  const assignmentFormDisabled = permissionDenied;

  const targetByOptions = useMemo(
    () =>
      ASSIGNMENT_TARGET_OPTIONS.map((option) => ({
        ...option,
        disabled:
          (option.value === "userIds" && !canUploadUserIds) ||
          (option.value === "mobileNo" && !canUploadMobile),
      })),
    [canUploadMobile, canUploadUserIds],
  );

  const selectedNotification = userNotifications.find(
    (item) => item.id === selectedNotificationId,
  );

  const loadUserNotifications = useCallback(async (forceRefresh = false) => {
    setListLoading(true);
    setListFetchError("");
    try {
      if (forceRefresh) {
        invalidateInAppNotificationListCache();
      }

      const allItems = await fetchAllInAppNotifications({
        includeExpired: true,
        targetType: "user-specific",
      });
      const items = allItems.filter(
        (item) =>
          matchesTargetType(item.targetType, "user-specific") &&
          !isNotificationExpired(item.expiry),
      );

      setUserNotifications(items);
      setPermissionDenied(false);

      setSelectedNotificationId((current) =>
        current && !items.some((item) => item.id === current) ? "" : current,
      );
    } catch (error) {
      const isPermission = isAssignmentPermissionError(error);
      const message = isPermission
        ? ASSIGNMENT_PERMISSION_ERROR_MESSAGE
        : getInAppNotificationErrorMessage(error, customConfig.FETCH_FAILURE);

      setListFetchError(message);
      setUserNotifications([]);
      setPermissionDenied(isPermission);
      setApiFeedbackMsg({
        open: true,
        type: "error",
        msg: message,
      });
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserNotifications();
  }, [loadUserNotifications]);

  const notificationOptions = userNotifications.map((item) => ({
    value: item.id,
    label: item.title,
    id: item.id,
  }));

  const listStatusMessage = listFetchError
    ? listFetchError
    : !listLoading && !userNotifications.length
      ? USER_SPECIFIC_NOTIFICATION_EMPTY_MESSAGE
      : "";

  const handleTargetByChange = (value) => {
    setTargetBy(value);
    setUserIds([]);
    setMobileNumbers([]);
    setErrors((prev) => ({ ...prev, targets: "" }));
    setCsvResetKey((prev) => prev + 1);
  };

  const handleCsvUpload = async (file, type) => {
    if (!file || assignmentFormDisabled) return;
    try {
      const values = await parseCsvFirstColumn(file);
      if (!values.length) {
        setApiFeedbackMsg({
          open: true,
          type: "error",
          msg: "CSV file is empty or invalid",
        });
        return;
      }

      if (type === "userIds") setUserIds((prev) => [...new Set([...prev, ...values])]);
      else setMobileNumbers((prev) => [...new Set([...prev, ...values])]);
      setErrors((prev) => ({ ...prev, targets: "" }));
    } catch {
      setApiFeedbackMsg({
        open: true,
        type: "error",
        msg: "Failed to parse CSV file",
      });
    }
  };

  const handleReset = useCallback(() => {
    setUserIds([]);
    setMobileNumbers([]);
    setSelectedNotificationId("");
    setTargetBy(getDefaultTargetBy(canUploadUserIds, canUploadMobile));
    setErrors({});
    setCsvResetKey((prev) => prev + 1);
  }, [canUploadMobile, canUploadUserIds]);

  const handleSave = async () => {
    if (assignmentFormDisabled || loading) return;

    const resolvedUserIds =
      targetBy === "userIds"
        ? userIdsInputRef.current?.commitPending?.() ?? userIds
        : userIds;
    const resolvedMobileNumbers =
      targetBy === "mobileNo"
        ? mobileNumbersInputRef.current?.commitPending?.() ?? mobileNumbers
        : mobileNumbers;

    const nextErrors = validateAssignmentForm({
      selectedNotificationId,
      targetBy,
      userIds: resolvedUserIds,
      mobileNumbers: resolvedMobileNumbers,
      canUploadUserIds,
      canUploadMobile,
    });

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      const validationMessage = nextErrors.notification || nextErrors.targets;
      if (validationMessage) {
        setApiFeedbackMsg({
          open: true,
          type: "error",
          msg: validationMessage,
        });
      }
      return;
    }

    const notificationId = selectedNotification?.id || selectedNotificationId;
    const body = buildConfigurePayload({
      userIds: targetBy === "userIds" ? resolvedUserIds : [],
      mobileNo: targetBy === "mobileNo" ? resolvedMobileNumbers : [],
    });

    setLoading(true);
    try {
      const response = await configureInAppNotification(notificationId, body);
      setApiFeedbackMsg({
        open: true,
        type: "success",
        msg: customConfig.UPDATE_SUCCESS,
      });
      handleReset();
      invalidateInAppNotificationListCache();
      await loadUserNotifications(true);
    } catch (error) {
      const isPermission = isAssignmentPermissionError(error);
      const message = getAssignmentErrorMessage(error, customConfig.UPDATE_FAILURE);

      if (isPermission) {
        setPermissionDenied(true);
        setListFetchError(ASSIGNMENT_PERMISSION_ERROR_MESSAGE);
      }

      setApiFeedbackMsg({
        open: true,
        type: "error",
        msg: message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <ScreenLoader message={customConfig.UPDATING_DATA} />}

      <Paper
        sx={{
          ...sectionCardSx,
          overflow: "hidden",
          border: `1px solid ${colors?.ghost?.light || "#E0E0E0"}`,
          position: "relative",
        }}
      >
        {listLoading && <ScreenLoader message={customConfig.FETCHING_DATA} />}

        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 2,
            borderBottom: `1px solid ${colors?.ghost?.light || "#E0E0E0"}`,
            bgcolor: "background.paper",
            ...inAppCardHeaderRowSx,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: 16, md: 18 } }}>
              Assign users to notification
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Select a user-specific notification and assign users by ID or mobile number.
            </Typography>
          </Box>
          <AppButton
            variant="primary"
            size="medium"
            startIcon={<RefreshIcon />}
            onClick={() => loadUserNotifications(true)}
            disabled={listLoading}
            sx={inAppPrimaryActionSx}
          >
            Refresh list
          </AppButton>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {listStatusMessage && (
            <Alert
              severity={listFetchError ? "error" : "info"}
              sx={{
                mb: 2.5,
                whiteSpace: "pre-line",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                "& .MuiAlert-action": {
                  ml: { xs: 0, sm: "auto" },
                  mt: { xs: 1, sm: 0 },
                  width: { xs: "100%", sm: "auto" },
                },
              }}
              action={
                listFetchError && !permissionDenied ? (
                  <AppButton
                    variant="primary"
                    size="medium"
                    onClick={() => loadUserNotifications(true)}
                    sx={inAppPrimaryActionSx}
                  >
                    Retry
                  </AppButton>
                ) : null
              }
            >
              {listStatusMessage}
            </Alert>
          )}

          <FormSection
            title="1. Notification"
            description="Choose which user-specific notification to assign"
          >
            <Box sx={inAppSectionIconRowSx}>
              <NotificationsActiveOutlinedIcon color="primary" sx={{ mt: 0.5 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <FormFieldRow
                  label="Notification"
                  required
                  responsiveStacked
                  hint="Only notifications with targetType = user-specific are listed"
                  error={errors.notification}
                >
                  <FormInput
                    id="assignment-notification-select"
                    type="select"
                    size="small"
                    fullWidth
                    value={selectedNotificationId}
                    onChange={(e) => {
                      setSelectedNotificationId(e.target.value);
                      setErrors((prev) => ({ ...prev, notification: "" }));
                    }}
                    displayEmpty
                    disabled={
                      assignmentFormDisabled ||
                      listLoading ||
                      (!!listFetchError && !userNotifications.length)
                    }
                    renderValue={(v) => {
                      if (!v) {
                        return notificationOptions.length
                          ? "Select notification"
                          : USER_SPECIFIC_NOTIFICATION_EMPTY_MESSAGE;
                      }
                      const item = userNotifications.find((n) => n.id === v);
                      return item?.title || v;
                    }}
                    renderOption={(option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ py: 0.5 }}>
                          <Typography variant="body2">{option.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            (ID: {option.id})
                          </Typography>
                        </Box>
                      </MenuItem>
                    )}
                    options={notificationOptions}
                    MenuProps={selectMenuProps}
                    error={!!errors.notification}
                  />
                </FormFieldRow>
                <SelectedNotificationSummary notification={selectedNotification} />
              </Box>
            </Box>
          </FormSection>

          <FormSection
            title="2. User targeting"
            description="Assign users by ID or mobile number"
          >
            <Box sx={inAppSectionIconRowSx}>
              <GroupAddOutlinedIcon color="primary" sx={{ mt: 0.5 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <FormFieldRow label="Target by" required responsiveStacked>
                  <AppRadioGroup
                    row
                    name="assignment-target-by"
                    value={targetBy}
                    onChange={(e) => handleTargetByChange(e.target.value)}
                    options={targetByOptions}
                    disabled={assignmentFormDisabled}
                    sx={inAppRadioGroupResponsiveSx}
                  />
                </FormFieldRow>

                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 1.5,
                    border: `1px solid ${colors?.ghost?.light || "#E0E0E0"}`,
                    bgcolor: "#fff",
                  }}
                >
                  {targetBy === "userIds" && (
                    <>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                        User IDs
                      </Typography>
                      <ChipTextInput
                        ref={userIdsInputRef}
                        key={`assignment-user-ids-${csvResetKey}`}
                        id="assignment-user-ids"
                        label="Enter user ID"
                        value={userIds}
                        onChange={(vals) => {
                          setUserIds(vals);
                          setErrors((prev) => ({ ...prev, targets: "" }));
                        }}
                        placeholder="Type user ID and press Enter"
                        disabled={assignmentFormDisabled || !canUploadUserIds}
                      />
                      <Box sx={{ mt: 2 }}>
                        <CsvUploadField
                          key={`user-ids-${csvResetKey}`}
                          id="assignment-user-ids-csv"
                          label="Import from CSV"
                          hint="First column or userId column"
                          onFileSelect={(file) => handleCsvUpload(file, "userIds")}
                          disabled={assignmentFormDisabled || !canUploadUserIds}
                        />
                      </Box>
                    </>
                  )}

                  {targetBy === "mobileNo" && (
                    <>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                        Mobile numbers
                      </Typography>
                      <ChipTextInput
                        ref={mobileNumbersInputRef}
                        key={`assignment-mobile-numbers-${csvResetKey}`}
                        id="assignment-mobile-numbers"
                        label="Enter mobile number"
                        value={mobileNumbers}
                        onChange={(vals) => {
                          setMobileNumbers(vals);
                          setErrors((prev) => ({ ...prev, targets: "" }));
                        }}
                        placeholder="Type mobile number and press Enter"
                        disabled={assignmentFormDisabled || !canUploadMobile}
                      />
                      <Box sx={{ mt: 2 }}>
                        <CsvUploadField
                          key={`mobile-${csvResetKey}`}
                          id="assignment-mobile-csv"
                          label="Import from CSV"
                          hint="First column or mobileNo column"
                          onFileSelect={(file) => handleCsvUpload(file, "mobileNo")}
                          disabled={assignmentFormDisabled || !canUploadMobile}
                        />
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </FormSection>
        </Box>

        <Divider />

        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 2,
            bgcolor: colors?.ghost?.lightest || "#FAFAFA",
            ...inAppFooterActionsSx,
          }}
        >
          <AppButton
            variant="ghost"
            size="medium"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleReset}
            disabled={loading}
          >
            Reset
          </AppButton>
          <AppButton
            variant="primary"
            size="medium"
            onClick={handleSave}
            loading={loading}
            disabled={assignmentFormDisabled || loading}
          >
            Assign users
          </AppButton>
        </Box>
      </Paper>

      <AppSnackbar
        open={apiFeedbackMsg.open}
        type={apiFeedbackMsg.type}
        message={apiFeedbackMsg.msg}
        onClose={() => setApiFeedbackMsg((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default UserAssignmentsTab;
